import React from 'react';
import {
	Container,
	Header,
	Box,
	Table,
} from '@amzn/awsui-components-react-v3';
import { useFormContext } from 'react-hook-form';
import { isEqual } from 'lodash';
import { DiffReviewStepProps } from './reviewForm.interface';

interface DiffRow {
	attributeId: string;
	attributeName: string;
	currentValue: unknown;
	previousValue: unknown;
	hasChanged: boolean;
}

const formatValue = (value: unknown): string => {
	if (value == null) return '-';

	return typeof value === 'string' ? value : JSON.stringify(value);
};

const columnDefinitions = [
	{
		id: 'attributeName',
		header: '',
		cell: (item: DiffRow) => item.attributeName,
	},
	{
		id: 'previousValue',
		header: 'Existing',
		cell: (item: DiffRow) => (
			<Box
				color={item.hasChanged ? 'text-status-error' : undefined}
				data-testid={`previous-value-${item.attributeId}`}
			>
				{formatValue(item.previousValue)}
			</Box>
		),
	},
	{
		id: 'newValue',
		header: 'New',
		cell: (item: DiffRow) => (
			<Box
				color={item.hasChanged ? 'text-status-success' : undefined}
				data-testid={`new-value-${item.attributeId}`}
			>
				{formatValue(item.currentValue)}
			</Box>
		),
	},
];

export default function DiffReviewForm({
	requirements,
	header,
	taskId = null,
	previousValues = {} as Record<string, unknown>,
}: DiffReviewStepProps) {
	const { getValues } = useFormContext();
	const formData = getValues();

	const currentData =
		(taskId ? formData[taskId] : formData.EntityData) ?? {};

	const editableRequirements = requirements.filter((req) => !req.value);

	const tableItems: DiffRow[] = editableRequirements.map(
		({ attributeId = '', attributeName = '' }) => {
			const currentValue =
				formData[attributeName] ?? currentData[attributeName];

			const previousValue = previousValues[attributeName];

			return {
				attributeId,
				attributeName,
				currentValue,
				previousValue,
				hasChanged: !isEqual(currentValue, previousValue),
			};
		},
	);

	return (
		<Container
			header={<Header>{header}</Header>}
			data-testid={`${taskId ?? 'common'}-diff-review-container`}
		>
			<Table
				columnDefinitions={columnDefinitions}
				items={tableItems}
				variant="embedded"
				stripedRows
				wrapLines
				data-testid={`${taskId ?? 'common'}-diff-table`}
			/>
		</Container>
	);
}
